import React, { useState, useEffect, useRef, useMemo } from "react";
import icons from "../../assets/images/icons";
import styles from "./Select.module.scss";
import { HorizontalFlex } from "../flex/HorizontalFlex";
import { BottomSheet } from "../material/Popup";
import { VerticalFlex } from "../flex/VerticalFlex";
import { useSelectHook } from "./useSelectHook";
import PropTypes from 'prop-types';
import RangeInput from "../input/RangeInput";
import { DefaultInputText,MinMaxInput } from "../input/InputText";
import { Range,getTrackBackground } from "react-range";
import { debounce } from 'lodash';
import {IconButton} from '../buttons/IconButton'
import buttonStyle from '../buttons/buttons.module.scss'

const REGION_STRUCTURE = {
    '경기도': {
      '수영시': ['수영음', '건사음'],
      '백화군': ['건열음'],
    },
    '서울시': {
      '강남구': ['논현동', '도곡동'],
      '성동구': ['화계동'],
    },
  };
  
const getDefaultLocation = () => {
  // 현재 지도의 위치에서 기본적으로 선택된 경우의 상황 문제 간단히 mock 해서 보입니다.
  return {
    province: '서울시',
    city: '강남구',
    district: '논현동',
  };
};

export function RegionSelector({ onConfirm }) {
  const [selected, setSelected] = useState(getDefaultLocation());
  const [provinces] = useState(Object.keys(REGION_STRUCTURE));
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    const province = selected.province;
    if (province) {
      const cityList = Object.keys(REGION_STRUCTURE[province] || {});
      setCities(cityList);
      setSelected((prev) => ({ ...prev, city: cityList[0], district: null }));
    }
  }, [selected.province]);

  useEffect(() => {
    const { province, city } = selected;
    if (province && city) {
      const districtList = REGION_STRUCTURE[province][city] || [];
      setDistricts(districtList);
      setSelected((prev) => ({ ...prev, district: districtList[0] }));
    }
    }, [selected.city]);
  
  const handleChange = (level, value) => {
    setSelected((prev) => ({ ...prev, [level]: value }));
  };
  
     const handleConfirm = () => {
    onConfirm(selected);
  };
  
   return (
  <div className="p-4 bg-white shadow-md rounded-xl w-full max-w-md">
    <h2 className="text-lg font-bold mb-4">경도 및 동 선택</h2>
  
       <div className="grid grid-cols-3 gap-2 text-center">
      <div>
        {provinces.map((p) => (
            <div
            key={p}
            className={`py-2 cursor-pointer ${selected.province === p ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}
            onClick={() => handleChange('province', p)}
           >
           {p}
         </div>
        ))}
     </div>
        <div>
        {cities.map((c) => (
         <div
           key={c}
           className={`py-2 cursor-pointer ${selected.city === c ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}
            onClick={() => handleChange('city', c)}
           >
               {c}
           </div>
         ))}
       </div>
       <div>
         {districts.map((d) => (
             <div
               key={d}
               className={`py-2 cursor-pointer ${selected.district === d ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}
               onClick={() => handleChange('district', d)}
              >
               {d}
             </div>
           ))}
         </div>
       </div>
        <button onClick={handleConfirm} className="mt-4 w-full">
          {selected.province} {selected.city} {selected.district} 으로 이동
        </button>
    </div>
   );
}
  


const DefaultSelectButton = ({
    className = "",
    isSelected = "",
    placeholder = "선택",
    onClick = () => { },
}) => {
    return (
        <div className={`${styles.defaultSelectButton} ${isSelected ? styles.selected : ''} ${className}`} onClick={onClick}>
            <span>{placeholder}</span>
            <img src={isSelected ? icons.arrow.down.blue : icons.arrow.down.black} ></img>
        </div>
    )
}


/**
 * DropdownSelect 컴포넌트
 *
 * 드롭다운 형태의 선택 UI를 렌더링합니다.
 *
 */

const DropdownSelect = ({
    className = "",
    placeholder = "선택",
    options = [],
    selected = [],
    multiple = false,
    onChange = () => { },
    labelSeperator = ','
}) => {
    const {
        isOpen,
        setIsOpen,
        togglePopover,
        selectedValues,
        selectAll,
        handleSelect,
        handleSelectAll,
        popoverRef,
    } = useSelectHook({
        selected: selected,
        options,
        multiple,
        onChange,
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        }
    }, [isOpen]);

    const [label, setLabel] = useState(placeholder);

    useEffect(() => {
        if (selectedValues != null && selectedValues.length > 0) {
            const selectedLabels = options
                .filter((option) => selectedValues.includes(option.value))
                .map((option) => option.label)
                .join(`${labelSeperator} `);
            setLabel(selectedLabels);
        } else {
            setLabel(placeholder);
        }
    }, [selectedValues])

    return (
        <div ref={popoverRef} className={`${styles.withPopover} ${className}`}>
            <DefaultSelectButton
                placeholder={label}
                onClick={togglePopover}
            />
            {isOpen && (
                <div className={`${styles.dropdownContainer} ${styles.popover}`}>
                    {options.map((option, index) => (
                        <button
                            key={index}
                            className={`${styles.item} ${selectedValues != null && selectedValues.includes(option.value) ? styles.selected : ""}`}
                            onClick={() => handleSelect(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

DropdownSelect.propTypes = {
    /** 추가 CSS 클래스 이름 */
    className: PropTypes.string,
    /** 선택 전 표시할 기본 텍스트 */
    placeholder: PropTypes.string,
    /** 선택 옵션 배열 ({ label, value } 형식) */
    options: PropTypes.arrayOf(
        PropTypes.shape({
            /** 옵션의 표시 텍스트 */
            label: PropTypes.string.isRequired,
            /** 옵션의 값 */
            value: PropTypes.any.isRequired,
        })
    ),
    /** 선택된 값 배열 */
    selected: PropTypes.array,
    /** 다중 선택 여부 */
    multiple: PropTypes.bool,
    /** 선택값 변경 시 호출되는 콜백 함수 */
    onChange: PropTypes.func,
    /** 선택된 옵션들을 구분할 구분자 */
    labelSeperator: PropTypes.string,
};



/**
 * PopoverChipSelect 컴포넌트
 *
 * 팝오버 형태의 칩 선택 UI를 렌더링합니다.
 *
 * @param {string} className - 추가 CSS 클래스 이름
 * @param {string} placeholder - 선택 전 표시할 기본 텍스트
 * @param {string} label - 선택창 상단에 표시할 라벨
 * @param {Array<{label: string, value: any}>} options - 선택 옵션 배열
 * @param {Array<any>} selected - 선택된 값 배열
 * @param {boolean} multiple - 다중 선택 여부
 * @param {function} onChange - 선택값 변경 시 호출되는 콜백 함수
 * @param {boolean} showAllOption - 전체 선택 옵션 표시 여부
 * @param {string} allOptionLabel - 전체 선택 옵션의 라벨
 * @returns {JSX.Element} - 팝오버 칩 선택 컴포넌트
 */
const PopoverChipSelect = ({
    className = "",
    placeholder = "선택",
    label = "",
    options = [],
    selected = [],
    multiple = false,
    onChange = () => { },
    showAllOption = false,
    allOptionLabel = "전체",
    isGroup = false,
    groups=[],
}) => {
    const {
        isOpen,
        setIsOpen,
        togglePopover,
        selectedValues,
        selectAll,
        handleSelect,
        handleSelectAll,
        popoverRef,
    } = useSelectHook({
        selected: selected,
        options,
        multiple,
        onChange,
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        }
    }, [isOpen]);

    return (
        <div ref={popoverRef} className={`${styles.withPopover} ${className}`}>
            <DefaultSelectButton
                isSelected={selectedValues != null && selectedValues.length > 0}
                placeholder={placeholder}
                onClick={togglePopover}
            />
            {isOpen && (
                <div className={`${styles.popoverChipContainer} ${styles.popover}`}>
                    <HorizontalFlex
                        className={`${styles.header}`}
                        leftChildren={
                            <>
                                <div className={`${styles.label}`}>{label}</div>
                                {multiple && <div className={`${styles.isMultipleLabel}`}>중복선택가능</div>}
                            </>
                        }
                        rightChildren={
                            <div className={`${styles.close}`} onClick={() => { setIsOpen(false) }}>
                                <img src={icons.close.black} width={17} height={17}></img>
                            </div>
                        }
                    />
                    <div className={`${styles.body}`}>
                        {multiple && showAllOption && (
                            <button
                                className={`${styles.chip} ${selectAll ? styles.selected : ""}`}
                                onClick={() => { if (!selectAll) { handleSelectAll(); } }}
                            >
                                {allOptionLabel}
                            </button>
                        )}

                        
                        
                    {isGroup ? (
                    groups.map((group, gIdx) => (
                        <div key={gIdx} className={styles.groupSection}>
                        <div className={styles.groupLabel}>{group.label}</div>
                        <div className={styles.optionWrapper}>
                            {group.options.map((option, index) => (
                            <button
                                key={index}
                                className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                                onClick={() => handleSelect(option.value)}
                            >
                                {option.label}
                            </button>
                            ))}
                        </div>
                        </div>
                    ))
                    ) : (
                    options.map((option, index) => (
                        <button
                        key={index}
                        className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                        onClick={() => handleSelect(option.value)}
                        >
                        {option.label}
                        </button>
                    ))
                    )}
                    </div>
                </div>
            )}
        </div>
    );
};

PopoverChipSelect.propTypes = {
    /** 추가 CSS 클래스 이름 */
    className: PropTypes.string,
    /** 선택 전 표시할 기본 텍스트 */
    placeholder: PropTypes.string,
    /** 선택창 상단에 표시할 라벨 */
    label: PropTypes.string,
    /** 선택 옵션 배열 ({ label, value } 형식) */
    options: PropTypes.arrayOf(
        PropTypes.shape({
            /** 옵션의 표시 텍스트 */
            label: PropTypes.string.isRequired,
            /** 옵션의 값 */
            value: PropTypes.any.isRequired,
        })
    ),
    /** 선택된 값 배열 */
    selected: PropTypes.array,
    /** 다중 선택 여부 */
    multiple: PropTypes.bool,
    /** 선택값 변경 시 호출되는 콜백 함수 */
    onChange: PropTypes.func,
    /** 전체 선택 옵션 표시 여부 */
    showAllOption: PropTypes.bool,
    /** 전체 선택 옵션의 라벨 */
    allOptionLabel: PropTypes.string,
};


/**
* BottomSheetSelect 컴포넌트
*
* 바텀시트 형태의 선택 UI를 렌더링합니다.
*
* @param {string} className - 추가 CSS 클래스 이름
* @param {string} placeholder - 선택 전 표시할 기본 텍스트
* @param {string} label - 선택창 상단에 표시할 라벨
* @param {Array<{label: string, value: any}>} options - 선택 옵션 배열
* @param {Array<any>} selected - 선택된 값 배열
* @param {boolean} multiple - 다중 선택 여부
* @param {function} onChange - 선택값 변경 시 호출되는 콜백 함수
* @param {boolean} showAllOption - 전체 선택 옵션 표시 여부
* @param {string} allOptionLabel - 전체 선택 옵션의 라벨
* @returns {JSX.Element} - 바텀시트 선택 컴포넌트
*/

const BottomSheetSelect = ({
    className = "",
    placeholder = "선택",
    label = "",
    options = [],
    selected = [],
    multiple = false,
    onChange = () => { },
    showAllOption = false,
    allOptionLabel = "전체",
}) => {
    const {
        isOpen,
        setIsOpen,
        togglePopover,
        selectedValues,
        selectAll,
        handleSelect,
        handleSelectAll,
        popoverRef,
    } = useSelectHook({
        selected: selected,
        options,
        multiple,
        onChange,
    });

    return (
        <div ref={popoverRef} className={className}>
            <DefaultSelectButton
                isSelected={selectedValues != null && selectedValues.length > 0}
                placeholder={placeholder}
                onClick={togglePopover}
            />

            <BottomSheet isOpen={isOpen} onClose={() => setIsOpen(false)}>
                <VerticalFlex className={styles.bottomSheetContainer}>
                    <HorizontalFlex
                        className={styles.header}
                        leftChildren={
                            <>
                                <div className={styles.label}>{label}</div>
                                {multiple && <div className={styles.isMultipleLabel}>중복 선택 가능</div>}
                            </>
                        }
                        rightChildren={
                            <div className={styles.close} onClick={() => setIsOpen(false)}>
                                <img src={icons.close.black} width={24} height={24} />
                            </div>
                        }
                    />
                    <div className={styles.body} expand={true}>
                        <div >
                            {multiple && showAllOption && (
                                <button
                                    className={`${styles.chip} ${selectAll ? styles.selected : ""}`}
                                    onClick={() => { if (!selectAll) { handleSelectAll(); } }}
                                >
                                    <div>
                                        {allOptionLabel}
                                    </div>
                                    <div className={`${styles.iconContainer}`}>
                                        {selectAll ? <img className={`${styles.checkIcon}`} src={icons.check.blue}></img> : null}
                                    </div>

                                </button>
                            )}

                            {options.map((option, index) => (
                                <button
                                    key={index}
                                    className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                                    onClick={() => handleSelect(option.value)}
                                >
                                    <div>
                                        {option.label}
                                    </div>
                                    <div className={`${styles.iconContainer}`}>
                                        {selectedValues != null && selectedValues.includes(option.value) && !selectAll ? <img className={`${styles.checkIcon}`} src={icons.check.blue}></img> : null}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                </VerticalFlex>
            </BottomSheet>
        </div>
    );
};


BottomSheetSelect.propTypes = {
    /** 추가 CSS 클래스 이름 */
    className: PropTypes.string,
    /** 선택 전 표시할 기본 텍스트 */
    placeholder: PropTypes.string,
    /** 선택창 상단에 표시할 라벨 */
    label: PropTypes.string,
    /** 선택 옵션 배열 ({ label, value } 형식) */
    options: PropTypes.arrayOf(
        PropTypes.shape({
            /** 옵션의 표시 텍스트 */
            label: PropTypes.string.isRequired,
            /** 옵션의 값 */
            value: PropTypes.any.isRequired,
        })
    ),
    /** 선택된 값 배열 */
    selected: PropTypes.array,
    /** 다중 선택 여부 */
    multiple: PropTypes.bool,
    /** 선택값 변경 시 호출되는 콜백 함수 */
    onChange: PropTypes.func,
    /** 전체 선택 옵션 표시 여부 */
    showAllOption: PropTypes.bool,
    /** 전체 선택 옵션의 라벨 */
    allOptionLabel: PropTypes.string,
};


//** section
// {
// hasTitle
// title
// min
// max
// maxLimit
// onMinChange
// onMaxChange
// } */


// const RangeSection = ({ 
//     section,
//     unit,
// }) => {
    
//     useEffect(() => {
//         const newMin = section.min ?? 0;
//         const newMax = section.max ?? section.maxLimit;
//         setRange([newMin, newMax]);
//       }, [section.min, section.max]);
//     const [range, setRange] = useState([
//       section.min ?? 0,
//       section.max ?? section.maxLimit
//     ]);
    
//     const debouncedUpdate = useMemo(
//         () =>
//           debounce(([newMin, newMax]) => {
//             section.onMinChange(newMin);
//             section.onMaxChange(newMax);
//           }, 300),
//         [section]
//       );
      
//     const handleMinChange = (val) => {
//         const parsed = Number(val);
      
//         if (val === '' || parsed === 0) {
//           setRange([0, range[1]]);
//           debouncedUpdate([null, range[1]]);
//           return;
//         }
      
//         const newMin = Math.min(parsed, range[1]);
//         setRange([newMin, range[1]]);
//         debouncedUpdate([newMin, range[1]]);
//       };
  
//       const handleMaxChange = (val) => {
//         const parsed = Number(val);
        
//         if (val === '' || parsed === 0) {
//           setRange([range[0], section.maxLimit]);
//           debouncedUpdate([range[0], null]); // null로 전달
//           return;
//         }
      
//         const newMax = Math.max(parsed, range[0]);
//         setRange([range[0], newMax]);
//         debouncedUpdate([range[0], newMax]);
//       };
      
  
//     return (
//       <div className={styles.priceSection}>
//         {section.hasTitle && <div className={styles.rangeTitle}>{section.title}</div>}
//         <HorizontalFlex>
//           <MinMaxInput
//             type="min"
//             counterpartValue={range[1]}
//             value={range[0] === 0 ? '' : range[0]}
//             onChange={handleMinChange}
//             unit={unit}
//           />
//           <span style={{ padding: "0 10px" }}>~</span>
//           <MinMaxInput
//             type="max"
//             counterpartValue={range[0]}
//             value={range[1] === section.maxLimit ? '' : range[1]}
//             onChange={handleMaxChange}
//             unit={unit}
//           />
//         </HorizontalFlex>
//         <Range
//           step={100}
//           min={0}
//           max={section.maxLimit}
//           values={range}
//           onChange={([newMin, newMax]) => {
//             setRange([newMin, newMax]);
//           }}

//           onFinalChange={([newMin, newMax]) => {
//             // 마우스 뗄 때 → 부모에게 최종 값 전달
//             section.onMinChange(newMin === 0 ? null : newMin);
//             section.onMaxChange(newMax === section.maxLimit ? null : newMax);
//           }}
//           renderTrack={({ props, children }) => (
//             <div
//               {...props}
//               className={styles.track}
//               style={{
//                 ...props.style,
//                 height: "6px",
//                 background: getTrackBackground({
//                     values: range,
//                     colors: ['#ddd', '#0080ff', '#ddd'],
//                     min: 0,
//                     max: section.maxLimit
//                   }),
//                 marginTop: "20px",
//               }}
//             >
//               {children}
//             </div>
//           )}
//           renderThumb={({ props, index }) => {
//             const { key, ...rest } = props;
//             return (
//               <div 
//                 key={key}
//                 {...rest}
//                 className={styles.thumb}
//                 style={{
//                   height: "20px",
//                   width: "20px",
//                   backgroundColor: "#FFF",
//                   borderRadius: "50%",
//                   fill: "#FFF",
//                   strokeWidth: "1px",
//                   stroke:"#EAEAEA",
//                   filter: "drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.15))",
//                   ...rest.style,
//                 }}
//               />
//             );
//           }}
//         />
//       </div>
//     );
//   };


// const RangeSelect = ({
//     className ="",
//     label = "가격",
//     placeholder="",
//     unit = "평",
//     changeUnit= "m²",
//     showUnitToggle = false,
//     sections = [],
//     onUnitChange = () => {},

// }) => {
//     const isSectionSelected = (sections) => {
//         return sections.some(section =>
//           section.min !== null || section.max !== null
//         );
//       };      
//     const [selectedUnit, setSelectedUnit] = useState(unit);
//     // 단위 변환 함수
//     // const convert = (value, fromUnit, toUnit) => {

//     //     console.log('value' + value);
//     //     console.log(fromUnit);
//     //     console.log('toUnit' + toUnit);

//     //     if (value == null) return null;
//     //     if (fromUnit === "평" && toUnit === "m²") return Math.round(value * 3.3058);
//     //     if (fromUnit === "m²" && toUnit === "평") return Math.round(value * 0.3025);
//     //     return value;
//     // };
//     // // 단위 변경 시 section 값까지 반영
//     // const handleUnitToggle = (newUnit) => {

//     //     console.log(newUnit);
//     //     console.log(selectedUnit);
//     //     if (newUnit === selectedUnit) return;
//     //     sections.forEach(section => {
//     //         const newMin = convert(section.min, selectedUnit, newUnit);
//     //         const newMax = convert(section.max, selectedUnit, newUnit);

//     //         console.log(newMin);
//     //         console.log(newMax);
//     //         if (section.onMinChange) section.onMinChange(newMin);
//     //         if (section.onMaxChange) section.onMaxChange(newMax);
//     //         console.log(section);
//     //     });
        
//     //     setSelectedUnit(newUnit);
//     // };
//     const handleUnitToggle = (newUnit) => {
//         setSelectedUnit(newUnit);
//     };
//     const [isOpen, setIsOpen] = useState(false);
//     const popoverRef = useRef(null);
//     const togglePopover = () => setIsOpen(prev => !prev);
//     // 외부 클릭 시 팝오버 닫기
//     useEffect(() => {
//         const handleClickOutside = (e) => {
//         if (popoverRef.current && !popoverRef.current.contains(e.target)) {
//             setIsOpen(false);
//         }
//         };
//         if (isOpen) document.addEventListener("mousedown", handleClickOutside);
//         return () => document.removeEventListener("mousedown", handleClickOutside);
//     }, [isOpen]);

//     return(
//         <div ref={popoverRef} className={`${styles.withPopover} ${className}`}>
//             <DefaultSelectButton
//                 placeholder={placeholder}
//                 onClick={togglePopover}
//                 isSelected={isSectionSelected(sections)}
//             />
//             {isOpen && (
//                 <div className={`${styles.popoverChipContainer} ${styles.popover}`}>
//                     <HorizontalFlex
//                         className={`${styles.header}`}
//                         leftChildren={
                            
//                             <>
//                                 <div className={`${styles.label}`}>
//                                     {label}
                                    
//                                 </div>
//                                 {showUnitToggle && (
//                                     <div className={styles.unitToggleWrapper}>
//                                         <button
//                                         className={`${styles.unitButton} ${selectedUnit === unit ? styles.active : ""}`}
//                                         onClick={() => handleUnitToggle(unit)}
//                                         >
//                                         평
//                                         </button>
//                                         <button
//                                         className={`${styles.unitButton} ${selectedUnit === changeUnit ? styles.active : ""}`}
//                                         onClick={() => handleUnitToggle(changeUnit)}
//                                         >
//                                         m²
//                                         </button>
//                                     </div>
//                                     )}
//                             </>
//                         }
//                         rightChildren={
//                             <div className={`${styles.close}`} onClick={() => { setIsOpen(false) }}>
//                                 <img src={icons.close.black} width={17} height={17}></img>
//                             </div>
//                         }
//                     />
//                     <div className={`${styles.body}`}>                  
//                         <VerticalFlex>
//                         {sections.map((section, idx) => (
//                             <RangeSection key={idx} section={section} unit={selectedUnit} />
//                         ))}
//                         </VerticalFlex>
//                     </div>
//                 </div>
//             )}
//         </div>
//     )
// }
const RangeSection = ({ section, unit }) => {
    const [range, setRange] = useState(() => {
        const min = section.min ?? 0;
        const rawMax = section.max;
        const max =
          rawMax == null || rawMax > section.rangeMaxLimit
            ? section.rangeMaxLimit
            : rawMax;
        return [min, max];
      });
  
    // // 외부 상태 변경 감지하여 내부도 반영
    // useEffect(() => {
    //     const min = section.min ?? 0;
    //     const rawMax = section.max;
    //     const max = rawMax == null || rawMax > section.rangeMaxLimit
    //       ? section.rangeMaxLimit
    //       : rawMax;

    //     setRange([min, max]);
    //   }, [section.min, section.max]);
  
    useEffect(() => {
        const min = section.min ?? 0;
        const rawMax = section.max;
      
        const isMinValid = min <= section.rangeMaxLimit;
        const isMaxValid = rawMax != null && rawMax <= section.rangeMaxLimit;
      
        const shouldReset =
          (rawMax != null && rawMax > section.rangeMaxLimit) ||
          (min > section.rangeMaxLimit) ||
          (rawMax != null && min > rawMax); // 정렬 조건도 포함
      
        if (shouldReset) {
          const safeMin = isMinValid ? min : 0;
          const safeMax = isMaxValid ? rawMax : section.rangeMaxLimit;
          setRange([safeMin, safeMax]);
          return;
        }
      
        const max = rawMax ?? section.rangeMaxLimit;
        setRange([min, max]);
      }, [section.min, section.max]);  

    const debouncedUpdate = useMemo(() => debounce(([min, max]) => {
      section.onMinChange(min);
      section.onMaxChange(max);
    }, 300), [section]);
  
    const handleMinChange = (val) => {
        const parsed = Number(val);
        const isEmpty = val === '' || parsed === 0;
      
        if (isEmpty) {
          // 최소값이 없으면 0으로 처리, 실제 전달은 null
          setRange([0, range[1]]);
          debouncedUpdate([null, section.max]);
          return;
        }
      
        if (parsed > section.rangeMaxLimit) {
          // 입력값이 범위를 초과할 경우:
          // 슬라이더는 리셋, 값은 그대로 전달
          setRange([0, section.rangeMaxLimit]);
          debouncedUpdate([parsed, section.max]);
          return;
        }
        
        console.log('----------')
        console.log(parsed)
        console.log(range[1])

        const newMin = Math.min(parsed, range[1]);
        setRange([newMin, range[1]]);
        debouncedUpdate([parsed, section.max]);
      };
    
    const handleMaxChange = (val) => {
        const parsed = Number(val);
        const isEmpty = val === '' || parsed === 0;
      
        if (isEmpty) {
          setRange([range[0], section.rangeMaxLimit]);
          debouncedUpdate([range[0], null]);
          return;
        }
      
        if (parsed > section.rangeMaxLimit) {
          // 슬라이더 리셋, 값은 그대로 보존
          setRange([0, section.rangeMaxLimit]);
          debouncedUpdate([range[0], parsed]); // 실제로 payload엔 초과 값 전달
          return;
        }
      
        const cappedMax = Math.min(parsed, section.rangeMaxLimit);
        setRange([range[0], cappedMax]);
        debouncedUpdate([range[0], parsed]);
      };

    // const handleMinChange = (val) => {
    //   const parsed = Number(val);
    //   const newMin = val === '' || parsed === 0 ? null : Math.min(parsed, range[1]);

    //   const max = section.max >range[1] ? section.max : range[1];

    //   setRange([newMin ?? 0, range[1]]);
    //   debouncedUpdate([newMin, section.max]);
    // };
  
    // const handleMaxChange = (val) => {
    //   const parsed = Number(val);

    //   const min = section.min > section.rangeMaxLimit ? section.min : range[0];

    //   // 입력이 없거나 0이면 null 처리
    //     if (val === '' || parsed === 0) {
    //         setRange([range[0], section.rangeMaxLimit]);
    //         debouncedUpdate([range[0], null]);
    //         return;
    //     }
    //     const cappedMax = Math.min(parsed, section.rangeMaxLimit);
    //     const newMax = Math.max(cappedMax, range[0]);
    //     setRange([range[0],cappedMax]);
    //     debouncedUpdate([range[0], parsed]);
    // };
  
    return (
      <div className={styles.priceSection}>
        {section.hasTitle && <div className={styles.rangeTitle}>{section.title}</div>}
        <HorizontalFlex>
          <MinMaxInput
            type="min"
            counterpartValue={section.maxLimit}
            // value={range[0] === 0 ? '' : range[0]}
            value={
                section.min > section.rangeMaxLimit
                  ? section.min
                  : range[0] === 0
                    ? ''
                    : range[0]
              }
            onChange={handleMinChange}
            unit={unit}
          />
          <span style={{ padding: "0 10px" }}>~</span>
          <MinMaxInput
            type="max"
            counterpartValue={range[0]}
            // value={range[1] === section.rangeMaxLimit ? '' : range[1]}
            value={
                section.max > section.rangeMaxLimit
                  ? section.max
                  : range[1] === section.rangeMaxLimit
                    ? ''
                    : range[1]
              }
            onChange={handleMaxChange}
            unit={unit}
          />
        </HorizontalFlex>
        <Range
          step={section.step}
          min={0}
          max={section.rangeMaxLimit}
          values={range}
          onChange={([newMin, newMax]) => {
            setRange([newMin, newMax])}}
          onFinalChange={([newMin, newMax]) => {
            section.onMinChange(newMin === 0 ? null : newMin);
            section.onMaxChange(newMax === section.rangeMaxLimit ? null : newMax);
          }}
          renderTrack={({ props, children }) => (
                        <div
                          {...props}
                          className={styles.track}
                          style={{
                            ...props.style,
                            height: "6px",
                            background: getTrackBackground({
                                values: range,
                                colors: ['#ddd', '#0080ff', '#ddd'],
                                min: 0,
                                max: section.rangeMaxLimit
                              }),
                            marginTop: "20px",
                          }}
                        >
                          {children}
                        </div>
                      )}
            renderThumb={({ props, index }) => {
                          const { key, ...rest } = props;
                          const value = range[index];
                          const limitedValue = Math.min(value, section.rangeMaxLimit);
                      

                          return (
                            <div 
                              key={key}
                              {...rest}
                              className={styles.thumb}
                              style={{
                                height: "20px",
                                width: "20px",
                                backgroundColor: "#FFF",
                                borderRadius: "50%",
                                fill: "#FFF",
                                strokeWidth: "1px",
                                stroke:"#EAEAEA",
                                filter: "drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.15))",
                                ...rest.style,
                              }}
                            />
                          );
                        }}             
        />
      </div>
    );
  };
  

const RangeSelect = ({
    className = "",
    label = "가격",
    placeholder = "",
    unit = "평",
    changeUnit = "m²",
    showUnitToggle = false,
    sections = [],
    onUnitChange = () => {},
  }) => {
    const [selectedUnit, setSelectedUnit] = useState(unit);
  
    const handleUnitToggle = (newUnit) => {
      if (newUnit === selectedUnit) return;
  
      // 단위 변환
      const convert = (value, from, to) => {
        if (value == null) return null;
        if (from === "평" && to === "m²") return Math.round(value * 3.3058);
        if (from === "m²" && to === "평") return Math.round(value * 0.3025);
        return value;
      };
  
      sections.forEach((section) => {
        if (section.min != null && section.onMinChange) {
          section.onMinChange(convert(section.min, selectedUnit, newUnit));
        }
        if (section.max != null && section.onMaxChange) {
          section.onMaxChange(convert(section.max, selectedUnit, newUnit));
        }
      });
  
      setSelectedUnit(newUnit);
      onUnitChange(newUnit); // 외부에서도 단위 업데이트 가능
    };
    
    const isSectionSelected = (sections) => {
        return sections.some(section =>
          section.min !== null || section.max !== null
        );
    };     
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);
    const togglePopover = () => setIsOpen(prev => !prev);
    // 외부 클릭 시 팝오버 닫기
    useEffect(() => {
        const handleClickOutside = (e) => {
        if (popoverRef.current && !popoverRef.current.contains(e.target)) {
            setIsOpen(false);
        }
        };
        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

  
    return (
      <div ref={popoverRef} className={`${styles.withPopover} ${className}`}>
        <DefaultSelectButton
          placeholder={placeholder}
          onClick={togglePopover}
          isSelected={isSectionSelected(sections)}
        />
        {isOpen && (
          <div className={`${styles.popoverChipContainer} ${styles.popover}`}>
            <HorizontalFlex
              className={`${styles.header}`}
              leftChildren={
                <>
                  <div className={`${styles.label}`}>{label}</div>
                  {showUnitToggle && (
                    <div className={styles.unitToggleWrapper}>
                      <button
                        className={`${styles.unitButton} ${selectedUnit === unit ? styles.active : ""}`}
                        onClick={() => handleUnitToggle(unit)}
                      >
                        평
                      </button>
                      <button
                        className={`${styles.unitButton} ${selectedUnit === changeUnit ? styles.active : ""}`}
                        onClick={() => handleUnitToggle(changeUnit)}
                      >
                        m²
                      </button>
                    </div>
                  )}
                </>
              }
              rightChildren={
                <div className={`${styles.close}`} onClick={() => setIsOpen(false)}>
                  <img src={icons.close.black} width={17} height={17} />
                </div>
              }
            />
            <div className={`${styles.body}`}>
              <VerticalFlex>
                {sections.map((section, idx) => (
                  <RangeSection key={idx} section={section} unit={selectedUnit} />
                ))}
              </VerticalFlex>
            </div>
          </div>
        )}
      </div>
    );
  };
  
const AllSelect = ({
    children,
    disabled = false,
    highlighted = false,
    src="",
    blueSrc = "",
    isIcon=false,
    placeholder,
    width = '40px',
    height = '40px',
    label = '',
    className,
    unitIndex,
    unitOptions,
    onToggleUnit,
    isSelected,
}) =>{
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);
    const togglePopover = () => setIsOpen(prev => !prev);
    const activeUnit = unitOptions?.[unitIndex];
    // 외부 클릭 시 팝오버 닫기
    useEffect(() => {
        const handleClickOutside = (e) => {

            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    return(
        <div
            ref={popoverRef}
            onMouseDown={isIcon ? togglePopover : undefined}
            className={
                isIcon
                ? `
                    
                    ${buttonStyle.iconButton}
                    ${buttonStyle.button}         
                    ${className}
                `
                : `${styles.withPopover} ${className}`
            }
            style={isIcon ? { height: width, width: height } : undefined}
        >
            {isIcon ? (
            src && (
                <div
                    style={isSelected ? {backgroundImage: `url(${blueSrc})`}: { backgroundImage: `url(${src})` }}
                    className={ `${buttonStyle.image} `}
                />
            )
            ) : (
            <DefaultSelectButton
                placeholder={placeholder}
                onClick={togglePopover}
                isSelected = {isSelected}
            />
            )}
            
            {isOpen && (
                <div className={`${styles.popoverChipContainer} ${styles.popover}`} onMouseDown={(e) => e.stopPropagation()}>
                    <HorizontalFlex
                        className={`${styles.header}`}
                        leftChildren={
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div className={`${styles.label}`}>{label}</div>
                                    <div className={styles.unitToggleWrapper}>
                                        {unitOptions && unitOptions.length > 0 && (
                                            unitOptions.map((unit, idx) => (
                                                <button
                                                key={unit.label}
                                                className={`${styles.unitButton} ${unitIndex === idx ? styles.active : ""}`}
                                                onClick={(e) => {
                                                    e.stopPropagation(); // 팝오버 닫힘 방지
                                                    if (unitIndex === idx) return;
                                                    onToggleUnit(idx);   // 특정 인덱스로 설정
                                                }}
                                                >
                                                {unit.label}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                
                                </div>
                            </>
                        }
                        rightChildren={
                            <div className={`${styles.close}`} onClick={() => { setIsOpen(false) }}>
                                <img src={icons.close.black} width={17} height={17}></img>
                            </div>
                        }
                    />
                    <div className={`${styles.body}`}>                  
                        <VerticalFlex>
                            {children}
                        </VerticalFlex>
                    </div>
                </div>
            )}
        </div>
    )

}

const SelectOrRange = ({
 type,
 multiple = true,
 showAllOption = true,
 allOptionLabel= '전체',
 options= [],
 sections = [],
 selected,
 selectTitle= '',
 onChange = () => {},
 unit = '',
}) => {

    const {
        isOpen,
        setIsOpen,
        togglePopover,
        selectedValues,
        selectAll,
        handleSelect,
        handleSelectAll,

    } = useSelectHook({
        selected: selected,
        options,
        multiple,
        onChange,
    });

    if (type === "select"){

        return(
            <>
             <div className={`${styles.body}`} style={{ padding: '0px', width: '100%', }}>
                <div className={styles.rangeTitle} style = {{margin: '0px',paddingTop: '0px'}}>{selectTitle}</div>
                {multiple && showAllOption && (
                    <button
                        className={`${styles.chip} ${selectAll ? styles.selected : ""}`}
                        onClick={() => { if (!selectAll) { handleSelectAll(); } }}
                    >
                        {allOptionLabel}
                    </button>
                )}

                {options.map((option, index) => (
                    <button
                        key={index}
                        className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                        onClick={() => handleSelect(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
            </>
        )
    }
    if (type === "range")
        return(
            <>
            {sections.map((section, idx) => (
                <RangeSection key={idx} section={section} unit={unit}/>
            ))}
            </>
        )

    return

}



const CustomSelectPopup = ({

   }) => {
   
       const {
           isOpen,
           setIsOpen,
           togglePopover,

   
       } = useSelectHook({});
   
       return(
        <>
         <div className={`${styles.body}`} style={{ padding: '0px', width: '100%', }}>
            <div className={styles.rangeTitle} style = {{margin: '0px',paddingTop: '0px'}}>{selectTitle}</div>
            {options.map((option, index) => (
                <button
                    key={index}
                    className={`${styles.chip} ${selectedValues != null && selectedValues.includes(option.value) && !selectAll ? styles.selected : ""}`}
                    onClick={() => handleSelect(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
        </>
    )
   }

export { DropdownSelect, PopoverChipSelect, BottomSheetSelect, RangeSelect, AllSelect,RangeSection, SelectOrRange, }